import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { Section } from "../../../components/Section";

import { type Participant, ParticipantRow } from "./ParticipantRow";
import { Table, type TableColumn } from "./Table";

/**
 * The Qualified Users table columns — single source of truth for alignment
 * AND responsive visibility across the header and every `ParticipantRow`'s
 * cells. Widths are owned by `ROW_GRID` (below), not per-column.
 *
 * Progressive collapse mirrors the Positions table exactly: instead of a
 * flex-per-column layout (fragile — a responsive Status width fought the name
 * toggle and the fixed gap never collapsed), header + rows share ONE responsive
 * CSS-grid template (`ROW_GRID`) that changes per `@container/qutable`
 * breakpoint. A `display:none` cell (`hideClassName`) drops out of the grid and
 * the remaining cells realign to the tracks listed at that width — the same
 * mechanism as Positions' `ROW_GRID` + per-cell `hidden @Nxl/postable:block`.
 *
 * As the table narrows, things drop in this priority order (widest → narrowest,
 * i.e. first-to-drop → last):
 *   1. Status label → dot only  (`ParticipantRow`; Status track shrinks
 *      88px→16px below `@4xl/qutable` — the FIRST thing to collapse)
 *   2. USDv Pending column       (track absent below `@3xl/qutable`)
 *   3. USDv Earned column        (track absent below `@2xl/qutable`)
 *   4. User name: full → first   (`ParticipantRow`, first-name below
 *      `@lg/qutable` — the LAST thing to collapse)
 * The narrowest row is `[dot] [First name] [USDv Balance]` — balance + dot +
 * first name are the last survivors. The inter-column gap also tightens as it
 * narrows (`gap-3 → gap-6 → gap-8`), so the Status↔Name gap collapses too.
 */
const COLUMNS: TableColumn[] = [
    // Status: dot + label. The label drops below @4xl (dot only) — the FIRST
    // thing to collapse; the grid's first track shrinks 88px→16px in lockstep
    // (see ROW_GRID) so the dot sits flush and Name reabsorbs the freed width.
    // The cell/track ALWAYS render (the dot is always shown) — only the header
    // text self-hides below @4xl (mirrors the Positions header's
    // `hidden @6xl/postable:block`), so a "Status" label never gets clipped by
    // the 16px dot-only track.
    {
        key: "status",
        label: <span className="hidden @4xl/qutable:inline">Status</span>
    },
    { key: "name", label: "Name" },
    { key: "balance", label: "USDv Balance", align: "right" },
    {
        key: "earned",
        label: "USDv Earned",
        align: "right",
        // Third to drop going narrow: revealed at ≥ @2xl container width.
        hideClassName: "hidden @2xl/qutable:block"
    },
    {
        key: "pending",
        label: "USDv Pending",
        align: "right",
        // Second to drop going narrow: revealed only at ≥ @3xl.
        hideClassName: "hidden @3xl/qutable:block"
    }
];

/**
 * The shared responsive grid template — applied identically to the header row
 * and every `ParticipantRow` (via the `Table` context) so columns line up and
 * hidden cells' tracks drop out. Ported from the Positions table's `ROW_GRID`.
 *
 * Tracks, narrow → wide (Status · Name · Balance [· Earned] [· Pending]):
 *   base (<@2xl): 16px dot · name · balance             (label off; first-name)
 *   @2xl:        16px dot · name · balance · earned
 *   @3xl:        16px dot · name · balance · earned · pending
 *   @4xl:        88px status · name · balance · earned · pending  (label on)
 * The Name track is `minmax(0,1fr)` and each money track is `minmax(160px,1fr)`,
 * so Name shares width equally with the money columns instead of dominating,
 * and the money columns grow. The Status track is `16px` (dot only) until
 * `@4xl` — where it widens to `minmax(88px,auto)` to fit the label (the FIRST
 * thing to collapse going narrow). The gap tightens as it narrows so the
 * Status↔Name gap collapses too.
 */
const ROW_GRID =
    // base (<@2xl): status-dot · name · balance
    "grid-cols-[16px_minmax(0,1fr)_minmax(160px,1fr)] " +
    // +earned
    "@2xl/qutable:grid-cols-[16px_minmax(0,1fr)_minmax(160px,1fr)_minmax(160px,1fr)] " +
    // +pending
    "@3xl/qutable:grid-cols-[16px_minmax(0,1fr)_minmax(160px,1fr)_minmax(160px,1fr)_minmax(160px,1fr)] " +
    // +status label (first track widens 16px→88px to fit the dot + label)
    "@4xl/qutable:grid-cols-[minmax(88px,auto)_minmax(0,1fr)_minmax(160px,1fr)_minmax(160px,1fr)_minmax(160px,1fr)] " +
    "items-center gap-3 @2xl/qutable:gap-6 @4xl/qutable:gap-8";

/** Sim-only forced-state seam for the Qualified Users table — targets ONE
 *  participant row by id for a scripted cursor hover-tour. Optional; when
 *  absent the table is fully inert (production default). */
export interface QualifiedUsersDemo {
    /** The participant id whose row should paint its forced hover, or null. */
    hoverId: string | null;
    /** Cursor-key prefix applied per row (`<prefix>-<id>`) so the sim's
     *  `moveTo` can travel to a row. */
    cursorKeyPrefix: string;
}

export interface QualifiedUsersProps {
    /** The already-resolved roster — holders or agents mapped to
     *  `Participant`s (incl. their per-row navigation closures). */
    participants: Participant[];
    /** Sim-only cursor-tour seam. Inert (undefined) in production. */
    demo?: QualifiedUsersDemo;
}

/**
 * QualifiedUsers — **View** (props-only). The active-program "Qualified Users"
 * section: the roster of holders or agents who qualify for the program, as a
 * read-only table. Composes the shared `Section` GC (heading) over a `Table`
 * GC body of `ParticipantRow`s, or the empty-state copy when the roster is
 * empty.
 *
 * Pure render: no roster selection, no navigation wiring. The Container
 * (`QualifiedUsersContainer`, app-side) selects holders vs agents per
 * `program.target`, maps them to `Participant`s (baking in the navigation),
 * and hands the resolved list here. This is the sim/View seam — the sim
 * renders this View with fixture participants and no router.
 *
 * Hoisted into `shared/widgets/yaas/programs` so external consumers (the
 * "+New Program" wiki doc) can render the bare Qualified Users section
 * fixture-driven, with no wallet / RPC / orgApiFetch. The app keeps a
 * re-export shim at the old path so `yaas-client` compiles unchanged.
 *
 * Rendered as the last section of the active-program read view.
 */
export function QualifiedUsers({
    participants,
    demo
}: QualifiedUsersProps): React.ReactElement {
    return (
        <Section heading="Qualified Users" className="py-8">
            {participants.length > 0 ? (
                <Table
                    columns={COLUMNS}
                    gridClassName={ROW_GRID}
                    className="mt-5"
                >
                    {participants.map((participant) => (
                        <ParticipantRow
                            key={participant.id}
                            participant={participant}
                            cursorKey={
                                demo
                                    ? `${demo.cursorKeyPrefix}-${participant.id}`
                                    : undefined
                            }
                            forceHover={demo?.hoverId === participant.id}
                        />
                    ))}
                </Table>
            ) : (
                <div className="px-4 mt-3">
                    <Text variant="small" className="text-foreground-muted">
                        No users qualify for this program yet.
                    </Text>
                </div>
            )}
        </Section>
    );
}
